import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppShell } from '@/components/AppShell';
import { BRAND_KITS_UI_VISIBLE } from '@/features/brandKits/brandKitsGate';
import { PageTopBar } from '@/components/PageTopBar';
import { services } from '@/services';
import { useAuthStore } from '@/stores/authStore';
import type { AdAccountSummary, BrandKit, DashboardSummary, Project } from '@/types';

import {
  HomeGreeting,
  HomeNeedsAction,
  HomeOnboarding,
  HomeRecentProjects,
  HomeSideLists,
} from './homePageSections';

/**
 * HomePage — 首頁儀表板(對齊 D3 `首頁.html`)。
 * -----------------------------------------------------------------------------
 * Layout:`<AppShell>` + `<PageTopBar title="首頁">`(D3 `.topbar`,**無分隔線**)
 * → 問候列 → 「需要你處理」大卡 → 2 欄(專案卡列 / 右欄清單卡)。
 *
 * 載入策略:`projects` + `brandKits` 併一支 `Promise.all`;`adsService.listAccounts()`
 * 與 `dashboardService.getDashboard()` **各自獨立** —— 那兩支對「未連結廣告 /
 * 無 current group」會 throw,併進主載入會把整頁拖垮。
 *
 * ⚠️ **誠實三態,不編數字**:BE 沒交付的聚合欄位一律**整張卡不畫**(不畫「—」、
 * 不畫 0、不編百分比)。畫 0 會被讀成「檢查過而且沒問題」,那比空白更糟。
 * 三個缺口(未檢查 / 可能被退件 / 建議調整)已列在 docs/54 §R292.17。
 *
 * ⚠️ R292.18 刪掉快照四格 / 本週成效 / 待處理清單(owner:「Ｄ三已經沒這些了」)。
 */
export function HomePage() {
  const { t } = useTranslation('home');
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [brandKits, setBrandKits] = useState<BrandKit[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  // null = 載入中(顯 skeleton);number = 真值。ads 未連結 / 無權限失敗 → 0(中性)。
  const [adAccounts, setAdAccounts] = useState<AdAccountSummary[] | null>(null);
  // T1(docs/49)— 首頁聚合(3 alert + 素材庫總數);null = 載入中 / 失敗(graceful)。
  const [dashboard, setDashboard] = useState<DashboardSummary | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  // T7/T9 — 本週成效 AEI 分布 bar;null = 載入中 / AEI 不可用(graceful 空態)。
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    let cancelled = false;
    // R397 — 品牌規範 UI 隱藏(brandKitsGate):gate 關 kits 恆空(側欄品牌區塊
    // `brandKits.length > 0` 自然不顯;Promise.all 形狀不動 — R314 syncing 判準看三支 fetch)。
    Promise.all([
      services.projectsService.list(),
      BRAND_KITS_UI_VISIBLE ? services.brandKitsService.list() : Promise.resolve([]),
    ])
      .then(([projectList, kitList]) => {
        if (cancelled) return;
        setProjects(projectList);
        setBrandKits(kitList);
        setLoadError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setProjects([]);
        setBrandKits([]);
        setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 廣告帳戶數:獨立 fetch(不併進上面 Promise.all)— ads 未連結 / 無權限會 throw,
  // 不能拖垮首頁主載入;失敗維持 0(對齊 AdsReportsPage listAccounts try/catch 容錯)。
  useEffect(() => {
    let cancelled = false;
    // R299 — 跨平台總覽(Google + Meta 一支拿完);總覽端點不在(BE 那批未併主線的環境)
    // 退回 Google 單平台清單 —— 與 R298 以前逐位元相同的請求。
    services.adsService
      .listAccountsOverview()
      .then((overview) => overview.accounts)
      .catch(() => services.adsService.listAccounts())
      .then((accounts) => {
        // R292.17 — 首頁右欄要逐筆列出,不只是計數。
        if (!cancelled) setAdAccounts(accounts);
      })
      .catch(() => {
        /* ads 未連結 / 無權限 → 退回 0(已連結但 0 帳戶與未連結對使用者語意相同) */
        if (!cancelled) setAdAccounts([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // T1 首頁聚合:獨立 fetch(不拖垮主載入);失敗(無 current group / BE 錯)→ 維持
  // null,卡片走 pending 而非永久 skeleton(dashboardLoading 區分)。
  useEffect(() => {
    let cancelled = false;
    services.dashboardService
      .getDashboard()
      .then((d) => {
        if (!cancelled) setDashboard(d);
      })
      .catch(() => {
        if (!cancelled) setDashboard(null);
      })
      .finally(() => {
        if (!cancelled) setDashboardLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);


  /**
   * 三支獨立 fetch 任一還在飛 = 還在同步。`null` 是本頁一致的「載入中」哨兵
   * (`projects` / `adAccounts` 都是,`dashboard` 另有 `dashboardLoading` 因為它
   * 失敗時也會停在 `null`)。
   *
   * ⚠️ 判準要涵蓋**全部三支** —— 只看 `projects` 的話,廣告帳戶與儀表板還在載時
   * 提示就已經收起來了,右欄卻仍是骨架 = 又回到「這是空的還是還在載」的歧義。
   */
  const syncing = projects === null || adAccounts === null || dashboardLoading;

  const brandKitById = useMemo(() => {
    const map = new Map<string, BrandKit>();
    for (const k of brandKits) map.set(k.brandKitId, k);
    return map;
  }, [brandKits]);

  // null = 載入中(顯 skeleton);載入完(成功或失敗皆已 setProjects)→ 真值。

  // user.name 取 displayName ?? email 前綴 ?? i18n greeting.guest(「訪客」)
  const userName = user?.displayName || user?.email?.split('@')[0] || t('greeting.guest');

  // T1 — 素材庫總數 / 待處理 KPI:載入中 → null(skeleton);失敗 → undefined(待後端);
  // 有值 → 真值。待處理 = 3 alert 計數總和(AEI null 視為 0)。

  /**
   * 零專案的**新手空狀態**(Bryan 2026-08-16;D3 `home.js` `secOnboard` / `.ob`)。
   *
   * 一個專案都沒有時,儀表板的四張快照卡、近期專案、每週成效、待辦全都是空殼 ——
   * 與其讓新使用者面對一整片 0,不如只留一件事:建立第一個專案。
   *
   * ⚠️ 判準用 `projects.length === 0` 而非 `!projects` —— `null` 是**載入中**,
   * 那時候閃一下空狀態再跳回儀表板會很突兀。
   */
  if (projects !== null && projects.length === 0 && !loadError) {
    return (
      <AppShell mainPadding={0}>
        <PageTopBar title={t('title')} />
        <div style={{ padding: '24px 40px 56px' }}>
          <HomeOnboarding userName={userName} />
        </div>
      </AppShell>
    );
  }

  return (
    // R292.11 — 首頁也走共用頁首(D3 `首頁.html`:`<h1 class="page-title">首頁</h1>` +
    // spring,**沒有分隔線**;PageTopBar 在沒有控制項時本來就不畫)。
    <AppShell mainPadding={0}>
      <PageTopBar title={t('title')} />
      <div data-testid="home-dashboard" style={{ padding: '24px 40px 56px' }}>
        <HomeGreeting userName={userName} syncing={syncing} />

        {/* 左欄 = 近期專案 → 需要你處理;右欄 = 本月用量 / 廣告帳戶 / 品牌規範。

            **R332(owner 2026-09-14)— 左欄兩塊對調**:原話「需要你處理的區塊往下移動,
            然後近期專案往上移動…避免用戶點擊專案的時候卡住」。「需要你處理」最多五張
            大卡(兩列),擺在上面等於每次進首頁都要先捲過它才點得到專案,而**進首頁最常
            做的事就是開專案**。

            ⚠️ **這條反轉了 R292.25 的「以『需要你處理』為平行線」**(當時 owner 的問題是
            那一區掛在格線**外面**滿版 → 右欄只能從近期專案那行開始,兩邊長相不同)。
            平行線本身沒有消失 —— 兩欄仍同在一個 grid、從同一條線開始,只是那條線現在
            對到的是「近期專案 ↔ 本月用量」。要改回舊序之前先看這段,不要把它當成
            R292.25 的回歸 bug。

            ⚠️ `HomeNeedsAction` **永遠畫一張卡**的行為維持不變(確認中 / 讀不到 / 待辦 /
            沒有待辦)。它原本的理由之一是對齊(整區回 null 會讓左欄第一個變成近期專案),
            對調之後那個理由消失了,但另一個理由還在而且更重要:**「讀不到」與「沒有待辦」
            是兩件事**,整區消失會被讀成後者(見該元件檔頭四態)。 */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: 20,
            alignItems: 'start',
          }}
        >
          {/* R332 — 對調之後區塊間距要由**容器**給:原本那 24px 來自 `HomeNeedsAction`
              自己的 `marginBottom`,它排在上面時剛好是兩塊之間的縫;排到下面就變成
              「兩塊黏在一起 + 底部多一條 24px」。故改 flex + gap(同右欄寫法),
              `HomeNeedsAction` 的 `wrapStyle` 也把那個 marginBottom 拿掉。 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24, minWidth: 0 }}>
            <HomeRecentProjects
              projects={projects}
              brandKitById={brandKitById}
              loadError={loadError}
            />
            <HomeNeedsAction
              dashboard={dashboard}
              loading={dashboardLoading}
              accounts={adAccounts}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
            {/* D3 右欄順序:本月用量 → 廣告帳戶 → 品牌規範。 */}
            <HomeSideLists
              accounts={adAccounts}
              brandKits={brandKits}
              projects={projects}
              usage={dashboard?.usage ?? null}
            />
          </div>
        </div>
      </div>
    </AppShell>
  );
}
